import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  dataTableHeadClass,
  DataTablePanel,
} from '../../components/DataTablePanel';
import { MydataPageShell } from './MydataPageShell';
import {
  Badge,
  Button,
  Callout,
  Card,
  ListEmptyState,
  LoadingSpinner,
  RefreshIcon,
} from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import type {
  MydataConnection,
  MydataCooldownReason,
  MydataSyncJob,
  MydataSyncJobItemStatus,
  MydataSyncJobStatus,
  MydataSyncProgress,
  MydataSyncRunItem,
  MydataSyncStepStatus,
} from '../../types/mydata';
import { formatDate, formatDateTime, formatMoney } from '../../utils/format';
import { MydataYearFilterBar } from './MydataYearFilterBar';

const SYNC_COOLDOWN_STORAGE_KEY = 'balanceflow:mydata_sync_cooldown_until';

function formatCountdown(totalSec: number): string {
  const sec = Math.max(0, Math.ceil(totalSec));
  if (sec < 60) return `${sec}δ`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}λ ${s}δ` : `${m}λ`;
}

function secondsUntilIso(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t0 = new Date(iso).getTime();
  if (Number.isNaN(t0)) return 0;
  return Math.max(0, Math.ceil((t0 - Date.now()) / 1000));
}

function rateLimitLiveMessage(
  labels: ReturnType<typeof t>['mydata'],
  seconds: number,
): string {
  return `${labels.rateLimitTitle} ${labels.rateLimitTryAgainBefore} ${Math.max(0, seconds)} ${labels.rateLimitTryAgainAfter}`;
}

function extractRetryAfterSeconds(err: unknown): number | null {
  if (!(err instanceof ApiError)) return null;

  const fromDetails = Number(err.details?.retryAfterSeconds);
  if (Number.isFinite(fromDetails) && fromDetails > 0) {
    return Math.ceil(fromDetails);
  }

  const patterns = [
    /σε περίπου\s+(\d+)\s+δευτερόλεπτα/i,
    /αναμονή\s+(\d+)\s*δ/i,
    /Try again in\s+(\d+)\s+seconds?/i,
    /(\d+)\s+δευτερόλεπτα/i,
  ];
  for (const pattern of patterns) {
    const match = pattern.exec(err.message);
    if (match) {
      const n = Number(match[1]);
      if (Number.isFinite(n) && n > 0) return Math.ceil(n);
    }
  }

  if (
    err.code === 'AADE_RATE_LIMIT' ||
    /rate limit/i.test(err.message) ||
    /περιόρισε τα αιτήματα/i.test(err.message)
  ) {
    return 180;
  }

  return null;
}

function readStoredCooldownSec(): number {
  try {
    const raw = sessionStorage.getItem(SYNC_COOLDOWN_STORAGE_KEY);
    if (!raw) return 0;
    const until = Number(raw);
    if (!Number.isFinite(until)) return 0;
    return Math.max(0, Math.ceil((until - Date.now()) / 1000));
  } catch {
    return 0;
  }
}

function persistCooldownSec(seconds: number) {
  try {
    if (seconds <= 0) {
      sessionStorage.removeItem(SYNC_COOLDOWN_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(
      SYNC_COOLDOWN_STORAGE_KEY,
      String(Date.now() + seconds * 1000),
    );
  } catch {
    // ignore storage errors
  }
}

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function monthRange(year: number, month: number): { from: string; to: string } {
  const lastDay = new Date(year, month, 0).getDate();
  return {
    from: `${year}-${pad2(month)}-01`,
    to: `${year}-${pad2(month)}-${pad2(lastDay)}`,
  };
}

function syncStepBadgeVariant(
  status: MydataSyncStepStatus,
): 'default' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'success':
      return 'success';
    case 'empty':
      return 'warning';
    case 'error':
      return 'danger';
    case 'running':
      return 'info';
    case 'skipped':
      return 'warning';
    default:
      return 'default';
  }
}

function syncStepStatusLabel(
  status: MydataSyncStepStatus,
  labels: ReturnType<typeof t>['mydata'],
): string {
  switch (status) {
    case 'pending':
      return labels.syncStepPending;
    case 'running':
      return labels.syncStepRunning;
    case 'success':
      return labels.syncStepSuccess;
    case 'empty':
      return labels.syncStepEmpty;
    case 'error':
      return labels.syncStepError;
    case 'skipped':
      return labels.syncStepSkipped;
    default:
      return status;
  }
}

function syncJobBadgeVariant(
  status: MydataSyncJobStatus,
): 'default' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'running':
      return 'info';
    case 'failed':
      return 'danger';
    case 'cancelled':
      return 'warning';
    default:
      return 'default';
  }
}

function syncJobStatusLabel(
  status: MydataSyncJobStatus,
  labels: ReturnType<typeof t>['mydata'],
): string {
  switch (status) {
    case 'pending':
      return labels.syncQueueJobPending;
    case 'running':
      return labels.syncQueueJobRunning;
    case 'completed':
      return labels.syncQueueJobCompleted;
    case 'cancelled':
      return labels.syncQueueJobCancelled;
    case 'failed':
      return labels.syncQueueJobFailed;
    default:
      return status;
  }
}

function syncJobItemBadgeVariant(
  status: MydataSyncJobItemStatus,
): 'default' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'success':
      return 'success';
    case 'running':
      return 'info';
    case 'error':
      return 'danger';
    case 'cancelled':
    case 'skipped':
      return 'warning';
    default:
      return 'default';
  }
}

function syncJobItemStatusLabel(
  status: MydataSyncJobItemStatus,
  labels: ReturnType<typeof t>['mydata'],
): string {
  switch (status) {
    case 'pending':
      return labels.syncQueueItemPending;
    case 'running':
      return labels.syncQueueItemRunning;
    case 'success':
      return labels.syncQueueItemSuccess;
    case 'error':
      return labels.syncQueueItemError;
    case 'cancelled':
      return labels.syncQueueItemCancelled;
    case 'skipped':
      return labels.syncQueueItemSkipped;
    default:
      return status;
  }
}

const AADE_BASE_URLS: Record<'sandbox' | 'production', string> = {
  sandbox: 'https://mydataapidev.aade.gr',
  production: 'https://mydatapi.aade.gr/myDATA',
};

function formatAadeDate(isoDate: string): string {
  const [y, m, d] = isoDate.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

function buildAadeRequest(
  environment: 'sandbox' | 'production',
  path: string,
  params: Record<string, string | undefined>,
): { url: string; params: Record<string, string> } {
  const cleaned: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') cleaned[key] = value;
  }
  const url = new URL(`${AADE_BASE_URLS[environment]}/${path}`);
  for (const [key, value] of Object.entries(cleaned)) {
    url.searchParams.set(key, value);
  }
  return { url: url.toString(), params: cleaned };
}

function initialSyncProgress(
  message: string,
  phase: MydataSyncProgress['phase'] = 'running',
  options?: {
    environment?: 'sandbox' | 'production';
    dateFrom?: string;
    dateTo?: string;
    entityVatNumber?: string | null;
  },
): MydataSyncProgress {
  const environment = options?.environment ?? 'sandbox';
  const dateFrom = options?.dateFrom
    ? formatAadeDate(options.dateFrom)
    : undefined;
  const dateTo = options?.dateTo ? formatAadeDate(options.dateTo) : undefined;
  const entityVatNumber = options?.entityVatNumber || undefined;

  const withRequest = (
    path: string,
    extra?: Record<string, string | undefined>,
  ) => {
    if (!dateFrom || !dateTo) {
      return {
        method: 'GET' as const,
        requestUrl: null,
        requestParams: null,
      };
    }
    const described = buildAadeRequest(environment, path, {
      ...extra,
      dateFrom,
      dateTo,
      entityVatNumber,
    });
    return {
      method: 'GET' as const,
      requestUrl: described.url,
      requestParams: described.params,
    };
  };

  return {
    message,
    phase,
    debugLog: [],
    steps: [
      {
        id: 'income',
        api: 'RequestMyIncome',
        label: 'Έσοδα',
        status: 'pending',
        detail: null,
        ...withRequest('RequestMyIncome'),
      },
      {
        id: 'expenses',
        api: 'RequestMyExpenses',
        label: 'Έξοδα',
        status: 'pending',
        detail: null,
        ...withRequest('RequestMyExpenses'),
      },
      {
        id: 'vat',
        api: 'RequestVatInfo',
        label: 'Πληροφορίες ΦΠΑ',
        status: 'pending',
        detail: null,
        ...withRequest('RequestVatInfo'),
      },
      {
        id: 'transmitted',
        api: 'RequestTransmittedDocs',
        label: 'Διαβιβασμένα παραστατικά',
        status: 'pending',
        detail: null,
        ...withRequest('RequestTransmittedDocs', { mark: '0' }),
      },
      {
        id: 'received',
        api: 'RequestDocs',
        label: 'Ληφθέντα παραστατικά',
        status: 'pending',
        detail: null,
        ...withRequest('RequestDocs', { mark: '0' }),
      },
    ],
  };
}

function formatDebugLogText(log: MydataSyncProgress['debugLog']): string {
  return log
    .map((entry) => {
      const lines = [
        `[${entry.at}] ${entry.kind.toUpperCase()} ${entry.method} ${entry.path}`,
        entry.message ? `message: ${entry.message}` : null,
        entry.status != null ? `status: ${entry.status}` : null,
        `url: ${entry.url}`,
        `params: ${JSON.stringify(entry.params)}`,
        `headers: ${JSON.stringify(entry.headers)}`,
        entry.bodyPreview ? `body:\n${entry.bodyPreview}` : null,
        '---',
      ].filter(Boolean);
      return lines.join('\n');
    })
    .join('\n');
}

function debugKindClass(
  kind: MydataSyncProgress['debugLog'][number]['kind'],
): string {
  switch (kind) {
    case 'request':
      return 'text-sky-300';
    case 'response':
      return 'text-emerald-300';
    case 'error':
      return 'text-rose-300';
    case 'db':
      return 'text-violet-300';
    default:
      return 'text-amber-200';
  }
}

function CooldownBanner({
  cooldownSec,
  cooldownReason,
  labels,
}: {
  cooldownSec: number;
  cooldownReason: MydataCooldownReason | null;
  labels: ReturnType<typeof t>['mydata'];
}) {
  if (cooldownSec <= 0) return null;
  const isPostSync = cooldownReason === 'post_sync';
  return (
    <div
      className={`shrink-0 rounded-lg border-2 px-4 py-3 shadow-sm ${
        isPostSync
          ? 'border-sky-500 bg-sky-50 text-sky-950 dark:border-sky-400 dark:bg-sky-950/40 dark:text-sky-50'
          : 'border-amber-500 bg-amber-50 text-amber-950 dark:border-amber-400 dark:bg-amber-950/40 dark:text-amber-50'
      }`}
    >
      <div className="text-sm font-semibold">
        {isPostSync ? labels.postSyncCooldownTitle : labels.rateLimitTitle}
      </div>
      <div className="mt-2 flex flex-wrap items-baseline gap-2 text-sm">
        <span>
          {isPostSync
            ? labels.postSyncCooldownBefore
            : labels.rateLimitTryAgainBefore}
        </span>
        <span
          className={`min-w-[2.5rem] text-center text-3xl font-bold tabular-nums ${
            isPostSync
              ? 'text-sky-700 dark:text-sky-300'
              : 'text-amber-700 dark:text-amber-300'
          }`}
        >
          {cooldownSec}
        </span>
        <span>
          {isPostSync
            ? labels.postSyncCooldownAfter
            : labels.rateLimitTryAgainAfter}
        </span>
      </div>
      <div
        className={`mt-2 text-xs ${
          isPostSync
            ? 'text-sky-800/90 dark:text-sky-100/80'
            : 'text-amber-800/90 dark:text-amber-100/80'
        }`}
      >
        {isPostSync
          ? labels.postSyncCooldownHint
          : labels.rateLimitCountdownHint}
      </div>
    </div>
  );
}

const SYNC_TECH_STORAGE_KEY = 'balanceflow:mydata_sync_tech_log';

export function MydataSyncPage() {
  const labels = t().mydata;
  const { isAdmin, isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const debugQuery =
    searchParams.get('debug') === '1' || searchParams.get('debug') === 'true';
  const canViewTechLog = isAdmin || isSuperAdmin || debugQuery;
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const initial = monthRange(now.getFullYear(), now.getMonth() + 1);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [connection, setConnection] = useState<MydataConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<MydataSyncProgress | null>(
    null,
  );
  const [showTechLog, setShowTechLog] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (
      new URLSearchParams(window.location.search).get('debug') === '1' ||
      new URLSearchParams(window.location.search).get('debug') === 'true'
    ) {
      return true;
    }
    return window.sessionStorage.getItem(SYNC_TECH_STORAGE_KEY) === '1';
  });
  const [debugCopied, setDebugCopied] = useState(false);

  useEffect(() => {
    if (debugQuery) setShowTechLog(true);
  }, [debugQuery]);
  const [cooldownSec, setCooldownSec] = useState(() => readStoredCooldownSec());
  const [cooldownReason, setCooldownReason] =
    useState<MydataCooldownReason | null>(() =>
      readStoredCooldownSec() > 0 ? 'rate_limit' : null,
    );
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [history, setHistory] = useState<MydataSyncRunItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [queueJobs, setQueueJobs] = useState<MydataSyncJob[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueBusy, setQueueBusy] = useState(false);
  const [queueMonths, setQueueMonths] = useState<number[]>([]);
  /** Tick so "starts in Xs" labels refresh while waiting. */
  const [queueClock, setQueueClock] = useState(() => Date.now());

  const beginCooldown = useCallback(
    (seconds: number, reason: MydataCooldownReason) => {
      const sec = Math.max(0, Math.ceil(seconds));
      persistCooldownSec(sec);
      setCooldownSec(sec);
      setCooldownReason(reason);
      setError(null);
    },
    [],
  );

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const page = await api.getMydataSyncHistory({ limit: 50 });
      setHistory(page.data);
    } catch {
      /* keep previous history on transient errors */
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      const page = await api.getMydataSyncJobs({ limit: 10 });
      setQueueJobs(page.data);
    } catch {
      /* keep previous queue on transient errors */
    } finally {
      setQueueLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const activeQueueJob = useMemo(
    () =>
      queueJobs.find(
        (j) => j.status === 'pending' || j.status === 'running',
      ) ?? queueJobs[0] ?? null,
    [queueJobs],
  );

  useEffect(() => {
    const needsPoll = queueJobs.some(
      (j) => j.status === 'pending' || j.status === 'running',
    );
    if (!needsPoll) return;
    const id = window.setInterval(() => {
      void loadQueue();
      void loadHistory();
    }, 4000);
    return () => window.clearInterval(id);
  }, [queueJobs, loadQueue, loadHistory]);

  useEffect(() => {
    const waiting = queueJobs.some(
      (j) =>
        j.status === 'pending' ||
        j.status === 'running' ||
        j.items.some((i) => i.status === 'pending'),
    );
    if (!waiting) return;
    const id = window.setInterval(() => setQueueClock(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [queueJobs]);

  const enqueueYear = useCallback(async () => {
    if (!connection?.configured) return;
    setQueueBusy(true);
    setError(null);
    setMessage(null);
    try {
      const job = await api.createMydataSyncJob({ year });
      setMessage(`${labels.syncQueueEnqueued}: ${job.label}`);
      await loadQueue();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setQueueBusy(false);
    }
  }, [connection?.configured, year, labels.syncQueueEnqueued, loadQueue]);

  const enqueueSelectedMonths = useCallback(async () => {
    if (!connection?.configured || queueMonths.length === 0) return;
    setQueueBusy(true);
    setError(null);
    setMessage(null);
    try {
      const job = await api.createMydataSyncJob({
        year,
        months: [...queueMonths].sort((a, b) => a - b),
      });
      setMessage(`${labels.syncQueueEnqueued}: ${job.label}`);
      setQueueMonths([]);
      await loadQueue();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setQueueBusy(false);
    }
  }, [
    connection?.configured,
    queueMonths,
    year,
    labels.syncQueueEnqueued,
    loadQueue,
  ]);

  const cancelQueueJob = useCallback(
    async (jobId: number) => {
      setQueueBusy(true);
      setError(null);
      try {
        await api.cancelMydataSyncJob(jobId);
        await loadQueue();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t().common.error);
      } finally {
        setQueueBusy(false);
      }
    },
    [loadQueue],
  );

  const toggleQueueMonth = useCallback((m: number) => {
    setQueueMonths((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
    );
  }, []);

  useEffect(() => {
    if (cooldownSec <= 0) {
      persistCooldownSec(0);
      if (cooldownReason) setCooldownReason(null);
      return;
    }
    const id = window.setTimeout(() => {
      setCooldownSec((s) => {
        const next = Math.max(0, s - 1);
        persistCooldownSec(next);
        return next;
      });
    }, 1000);
    return () => window.clearTimeout(id);
  }, [cooldownSec, cooldownReason]);

  useEffect(() => {
    if (cooldownSec <= 0 || !cooldownReason) return;
    const mydataLabels = t().mydata;
    const live =
      cooldownReason === 'post_sync'
        ? `${mydataLabels.postSyncCooldownTitle} ${mydataLabels.postSyncCooldownBefore} ${cooldownSec} ${mydataLabels.postSyncCooldownAfter}`
        : rateLimitLiveMessage(mydataLabels, cooldownSec);
    setSyncProgress((prev) =>
      prev && (prev.phase === 'error' || prev.phase === 'done')
        ? { ...prev, message: live }
        : prev,
    );
  }, [cooldownSec, cooldownReason]);

  const applyMonth = useCallback((y: number, m: number) => {
    const range = monthRange(y, m);
    setYear(y);
    setMonth(m);
    setDateFrom(range.from);
    setDateTo(range.to);
  }, []);

  const syncPreviewOptions = useCallback(
    () => ({
      environment: connection?.environment ?? ('sandbox' as const),
      dateFrom,
      dateTo,
      entityVatNumber: connection?.vatNumber ?? null,
    }),
    [connection?.environment, connection?.vatNumber, dateFrom, dateTo],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [conn, sum] = await Promise.all([
          api.getMydataConnection(),
          api.getMydataSummary({ dateFrom, dateTo }),
        ]);
        if (cancelled) return;
        setConnection(conn);
        setLastSyncedAt(sum.syncedAt);
        const serverCooldown = Number(conn.syncCooldownSeconds ?? 0);
        if (serverCooldown > 0) {
          setCooldownSec((prev) => {
            const next = Math.max(prev, serverCooldown);
            persistCooldownSec(next);
            return next;
          });
          setCooldownReason((prev) => prev ?? 'rate_limit');
        }
        setSyncProgress((prev) => {
          if (prev && (prev.phase === 'running' || syncing)) return prev;
          return initialSyncProgress(labels.syncModalReady, 'idle', {
            environment: conn.environment,
            dateFrom,
            dateTo,
            entityVatNumber: conn.vatNumber,
          });
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, labels.syncModalReady, syncing]);

  const runSync = useCallback(async () => {
    if (syncing || cooldownSec > 0) return;
    setSyncing(true);
    setError(null);
    setMessage(null);
    setSyncProgress(
      initialSyncProgress(
        labels.syncStarting,
        'running',
        syncPreviewOptions(),
      ),
    );
    const pollId = window.setInterval(() => {
      void api
        .getMydataConnection()
        .then((conn) => {
          if (conn.syncProgress) {
            setSyncProgress(conn.syncProgress);
          }
        })
        .catch(() => {
          /* ignore poll errors during sync */
        });
    }, 1000);
    try {
      const sum = await api.syncMydata({ dateFrom, dateTo });
      if (sum.syncProgress) {
        setSyncProgress(sum.syncProgress);
      } else {
        setSyncProgress((prev) =>
          prev
            ? {
                ...prev,
                message: labels.synced,
                phase: 'done',
              }
            : prev,
        );
      }
      setLastSyncedAt(sum.syncedAt);
      const conn = await api.getMydataConnection();
      setConnection(conn);
      if (conn.syncProgress) {
        setSyncProgress(conn.syncProgress);
      }
      setMessage(labels.synced);
      const coolSec = Number(sum.cooldownSeconds);
      if (Number.isFinite(coolSec) && coolSec > 0) {
        beginCooldown(coolSec, sum.cooldownReason ?? 'post_sync');
      }
      await loadHistory();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t().common.error;
      const retryAfter = extractRetryAfterSeconds(err);
      if (retryAfter != null) {
        beginCooldown(retryAfter, 'rate_limit');
        setSyncProgress((prev) =>
          prev
            ? {
                ...prev,
                message: rateLimitLiveMessage(labels, retryAfter),
                phase: 'error',
              }
            : initialSyncProgress(
                rateLimitLiveMessage(labels, retryAfter),
                'error',
                syncPreviewOptions(),
              ),
        );
      } else {
        setError(msg);
        setSyncProgress((prev) =>
          prev
            ? {
                ...prev,
                message: msg,
                phase: 'error',
              }
            : initialSyncProgress(msg, 'error', syncPreviewOptions()),
        );
      }
      try {
        const conn = await api.getMydataConnection();
        if (conn.syncProgress) {
          setSyncProgress(conn.syncProgress);
        }
      } catch {
        /* ignore */
      }
      await loadHistory();
    } finally {
      window.clearInterval(pollId);
      setSyncing(false);
    }
  }, [
    beginCooldown,
    cooldownSec,
    dateFrom,
    dateTo,
    labels,
    loadHistory,
    syncPreviewOptions,
    syncing,
  ]);

  const configured = connection?.configured ?? false;
  const previewSteps =
    syncProgress?.steps ??
    initialSyncProgress(labels.syncModalReady, 'idle', syncPreviewOptions())
      .steps;

  return (
    <MydataPageShell>
      <div className="shrink-0 space-y-1">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {labels.syncHint}
        </p>
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <Link to="/mydata" className="text-brand hover:underline">
            {labels.periodTitle}
          </Link>
          {' · '}
          <Link to="/mydata/debts" className="text-brand hover:underline">
            {labels.sectionDebts}
          </Link>
          {' · '}
          <Link to="/mydata/documents" className="text-brand hover:underline">
            {labels.documents}
          </Link>
          {' · '}
          <Link
            to="/settings/mydata-connection"
            className="text-brand hover:underline"
          >
            {labels.connectionTitle}
          </Link>
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {configured ? (
          <Badge variant="success">{labels.connectionConfigured}</Badge>
        ) : (
          <Badge variant="warning">{labels.connectionMissing}</Badge>
        )}
        {connection?.environment ? (
          <Badge variant="secondary">
            {labels.environmentLabel}:{' '}
            {connection.environment === 'production'
              ? labels.production
              : labels.sandbox}
          </Badge>
        ) : null}
        {lastSyncedAt ? (
          <span className="text-xs text-slate-500">
            {labels.freshFromAade} · {formatDateTime(lastSyncedAt)}
          </span>
        ) : (
          <span className="text-xs text-slate-500">{labels.neverSynced}</span>
        )}
      </div>

      {!configured ? (
        <Callout variant="warning" className="shrink-0">
          {labels.notConfigured}{' '}
          <Link to="/settings/mydata-connection" className="underline">
            {labels.connectionTitle}
          </Link>
        </Callout>
      ) : null}

      {error && cooldownSec <= 0 ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}
      {message ? (
        <Callout variant="success" className="shrink-0">
          {message}
        </Callout>
      ) : null}

      <CooldownBanner
        cooldownSec={cooldownSec}
        cooldownReason={cooldownReason}
        labels={labels}
      />

      <MydataYearFilterBar
        year={year}
        onYearChange={(y) => applyMonth(y, month)}
        actions={
          <Button
            type="button"
            disabled={syncing || !configured || cooldownSec > 0}
            onClick={() => void runSync()}
          >
            <RefreshIcon className="h-4 w-4" />
            {syncing
              ? labels.syncing
              : cooldownSec > 0
                ? `${
                    cooldownReason === 'post_sync'
                      ? labels.postSyncCooldownBefore
                      : labels.rateLimitCountdown
                  } ${formatCountdown(cooldownSec)}`
                : labels.syncModalStart}
          </Button>
        }
      />

      <Card className="shrink-0 space-y-3 p-4">
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {labels.syncQueueTitle}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {labels.syncQueueHint}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {labels.syncQueueRateLimitNote}
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={!configured || queueBusy}
            onClick={() => void enqueueYear()}
          >
            {labels.syncQueueEnqueueYear} ({year})
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!configured || queueBusy || queueMonths.length === 0}
            onClick={() => void enqueueSelectedMonths()}
          >
            {labels.syncQueueEnqueueMonths}
            {queueMonths.length > 0 ? ` (${queueMonths.length})` : ''}
          </Button>
          {activeQueueJob &&
          (activeQueueJob.status === 'pending' ||
            activeQueueJob.status === 'running') ? (
            <Button
              type="button"
              variant="secondary"
              disabled={queueBusy}
              onClick={() => void cancelQueueJob(activeQueueJob.id)}
            >
              {labels.syncQueueCancel}
            </Button>
          ) : null}
        </div>

        <div>
          <div className="mb-1.5 text-xs font-medium text-slate-500">
            {labels.syncQueueSelectMonths}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
              const selected = queueMonths.includes(m);
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleQueueMonth(m)}
                  className={[
                    'rounded-md border px-2 py-1 text-xs font-medium transition-colors',
                    selected
                      ? 'border-brand bg-brand-soft text-brand'
                      : 'border-line text-ink-muted hover:border-brand/30',
                  ].join(' ')}
                >
                  {labels.monthNamesShort[m - 1] ?? m}
                </button>
              );
            })}
          </div>
        </div>

        {queueLoading ? (
          <LoadingSpinner />
        ) : !activeQueueJob ? (
          <p className="text-sm text-slate-500">{labels.syncQueueEmpty}</p>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                {activeQueueJob.label}
              </div>
              <Badge variant={syncJobBadgeVariant(activeQueueJob.status)}>
                {syncJobStatusLabel(activeQueueJob.status, labels)}
              </Badge>
            </div>
            <div className="text-xs text-slate-500">
              {labels.syncQueueProgress}: {activeQueueJob.successItems}/
              {activeQueueJob.totalItems}
              {activeQueueJob.errorItems > 0
                ? ` · ${labels.syncStatusError}: ${activeQueueJob.errorItems}`
                : ''}
              {(() => {
                void queueClock;
                const next = activeQueueJob.items.find(
                  (i) => i.status === 'pending',
                );
                if (!next) return null;
                const wait = secondsUntilIso(next.availableAt);
                if (wait <= 0) return null;
                return (
                  <>
                    {' · '}
                    {labels.syncQueueNextStart}: {formatCountdown(wait)}
                  </>
                );
              })()}
            </div>
            <div className="max-h-56 overflow-auto rounded-lg border border-slate-200 dark:border-slate-700">
              <table className="min-w-full text-left text-xs">
                <thead className={dataTableHeadClass}>
                  <tr>
                    <th className="px-2 py-1.5">{labels.syncHistoryPeriod}</th>
                    <th className="px-2 py-1.5">{labels.syncHistoryStatus}</th>
                    <th className="px-2 py-1.5">{labels.syncQueueStartsAt}</th>
                    <th className="px-2 py-1.5 text-right">
                      {labels.incomeNet}
                    </th>
                    <th className="px-2 py-1.5 text-right">
                      {labels.expenseNet}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {activeQueueJob.items.map((item) => {
                    const waitSec =
                      item.status === 'pending'
                        ? secondsUntilIso(item.availableAt)
                        : 0;
                    return (
                      <tr
                        key={item.id}
                        className="border-t border-slate-100 dark:border-slate-800"
                      >
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          {formatDate(item.dateFrom)} –{' '}
                          {formatDate(item.dateTo)}
                        </td>
                        <td className="px-2 py-1.5">
                          <Badge
                            variant={syncJobItemBadgeVariant(item.status)}
                          >
                            {syncJobItemStatusLabel(item.status, labels)}
                          </Badge>
                          {item.errorMessage ? (
                            <div className="mt-0.5 max-w-[14rem] truncate text-[10px] text-rose-600">
                              {item.errorMessage}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">
                          {item.status === 'running'
                            ? labels.syncQueueItemRunning
                            : item.status === 'pending'
                              ? waitSec > 0
                                ? `${labels.syncQueueStartsIn} ${formatCountdown(waitSec)}`
                                : labels.syncQueueJobPending
                              : item.finishedAt
                                ? formatDateTime(item.finishedAt)
                                : '—'}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {item.incomeNet != null
                            ? formatMoney(item.incomeNet)
                            : '—'}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {item.expenseNet != null
                            ? formatMoney(item.expenseNet)
                            : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      {syncing && !showTechLog ? (
        <Callout variant="info" className="shrink-0">
          {syncProgress?.message || labels.syncSimpleRunning}
        </Callout>
      ) : null}

      {canViewTechLog ? (
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {labels.syncTechHint}
          </p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => {
              setShowTechLog((prev) => {
                const next = !prev;
                window.sessionStorage.setItem(
                  SYNC_TECH_STORAGE_KEY,
                  next ? '1' : '0',
                );
                return next;
              });
            }}
          >
            {showTechLog ? labels.syncTechHide : labels.syncTechShow}
          </Button>
        </div>
      ) : null}

      <div
        className={[
          'grid min-h-0 shrink-0 gap-4 pb-4',
          showTechLog && canViewTechLog ? 'lg:grid-cols-2' : '',
        ].join(' ')}
      >
        {showTechLog && canViewTechLog ? (
          <section className="min-w-0 space-y-3">
            {loading && !syncing ? (
              <LoadingSpinner />
            ) : (
              <Card className="space-y-4 p-4">
                <div>
                  <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {labels.syncModalTitle}
                  </h2>
                  {cooldownSec <= 0 ? (
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                      {syncProgress?.message || labels.syncModalReady}
                    </p>
                  ) : null}
                  {syncProgress?.phase !== 'idle' && cooldownSec <= 0 ? (
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {labels.syncProgressHint}
                    </p>
                  ) : null}
                </div>

                <ul className="space-y-3">
                  {previewSteps.map((step) => (
                    <li
                      key={step.id}
                      className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-700 dark:bg-slate-800/50"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                            {step.label}
                          </div>
                          <div className="mt-0.5 font-mono text-xs text-slate-500 dark:text-slate-400">
                            {labels.syncRequestMethod}: {step.method || 'GET'} ·{' '}
                            {labels.syncApiLabel}: {step.api}
                          </div>
                        </div>
                        <Badge variant={syncStepBadgeVariant(step.status)}>
                          {syncStepStatusLabel(step.status, labels)}
                        </Badge>
                      </div>
                      {step.requestUrl ? (
                        <div className="mt-2 space-y-1">
                          <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                            {labels.syncRequestUrl}
                          </div>
                          <div className="break-all rounded border border-slate-200 bg-white px-2 py-1.5 font-mono text-[11px] leading-relaxed text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200">
                            {step.requestUrl}
                          </div>
                        </div>
                      ) : null}
                      {step.requestParams ? (
                        <div className="mt-2 space-y-1">
                          <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                            {labels.syncRequestParams}
                          </div>
                          {Object.keys(step.requestParams).length === 0 ? (
                            <div className="text-xs text-slate-500">
                              {labels.syncNoParams}
                            </div>
                          ) : (
                            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded border border-slate-200 bg-white px-2 py-1.5 font-mono text-[11px] dark:border-slate-600 dark:bg-slate-900">
                              {Object.entries(step.requestParams).map(
                                ([key, value]) => (
                                  <div key={key} className="contents">
                                    <dt className="text-slate-500">{key}</dt>
                                    <dd className="break-all text-slate-800 dark:text-slate-200">
                                      {value}
                                    </dd>
                                  </div>
                                ),
                              )}
                            </dl>
                          )}
                        </div>
                      ) : null}
                      {step.detail ? (
                        <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                          {step.detail}
                        </div>
                      ) : step.status === 'running' ? (
                        <div className="mt-2 text-sm text-slate-500">
                          {labels.syncStepRunning}…
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>

                <div className="space-y-2 border-t border-slate-200 pt-4 dark:border-slate-700">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {labels.syncDebugTitle}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={!syncProgress?.debugLog?.length}
                      onClick={() => {
                        const text = formatDebugLogText(
                          syncProgress?.debugLog ?? [],
                        );
                        void navigator.clipboard.writeText(text).then(() => {
                          setDebugCopied(true);
                          window.setTimeout(() => setDebugCopied(false), 1600);
                        });
                      }}
                    >
                      {debugCopied
                        ? labels.syncDebugCopied
                        : labels.syncDebugCopy}
                    </Button>
                  </div>
                  <div className="max-h-72 overflow-auto rounded-lg border border-slate-800 bg-slate-950 p-3 font-mono text-[11px] leading-relaxed text-slate-200">
                    {(syncProgress?.debugLog?.length ?? 0) === 0 ? (
                      <div className="text-slate-500">
                        {labels.syncDebugEmpty}
                      </div>
                    ) : (
                      <ul className="space-y-3">
                        {syncProgress!.debugLog.map((entry, index) => (
                          <li
                            key={`${entry.at}-${entry.kind}-${entry.path}-${index}`}
                            className="border-b border-slate-800 pb-3 last:border-0 last:pb-0"
                          >
                            <div
                              className={`font-semibold ${debugKindClass(entry.kind)}`}
                            >
                              [{new Date(entry.at).toLocaleTimeString('el-GR')}]{' '}
                              {entry.kind === 'db'
                                ? `${labels.syncDebugDb} ${entry.method}`
                                : `${entry.kind.toUpperCase()} ${entry.method}`}{' '}
                              {entry.path}
                              {entry.status != null ? ` → ${entry.status}` : ''}
                            </div>
                            {entry.message ? (
                              <div className="mt-1 text-slate-300">
                                {entry.message}
                              </div>
                            ) : null}
                            {entry.kind !== 'db' ? (
                              <div className="mt-1 break-all text-slate-400">
                                {entry.url}
                              </div>
                            ) : null}
                            {Object.keys(entry.params).length > 0 ? (
                              <div className="mt-1 text-slate-500">
                                {labels.syncRequestParams}:{' '}
                                {JSON.stringify(entry.params)}
                              </div>
                            ) : null}
                            {entry.kind !== 'db' ? (
                              <div className="mt-1 text-slate-500">
                                {labels.syncDebugHeaders}:{' '}
                                {JSON.stringify(entry.headers)}
                              </div>
                            ) : null}
                            {entry.bodyPreview ? (
                              <pre className="mt-2 whitespace-pre-wrap break-all text-slate-300">
                                {entry.kind === 'db'
                                  ? labels.syncDebugDb
                                  : labels.syncDebugBody}
                                :{'\n'}
                                {entry.bodyPreview}
                              </pre>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </Card>
            )}
          </section>
        ) : null}

        <section className="min-w-0 space-y-3">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {labels.syncHistoryTitle}
          </h2>
          {historyLoading && history.length === 0 ? (
            <LoadingSpinner />
          ) : (
            <DataTablePanel>
              {history.length === 0 ? (
                <ListEmptyState title={labels.syncHistoryEmpty} />
              ) : (
                <div className="max-h-[40rem] overflow-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className={dataTableHeadClass}>
                      <tr>
                        <th className="px-3 py-2">{labels.syncHistoryWhen}</th>
                        <th className="px-3 py-2">{labels.syncHistoryPeriod}</th>
                        <th className="px-3 py-2">{labels.syncHistoryStatus}</th>
                        <th className="px-3 py-2 text-right">
                          {labels.syncHistoryDocuments}
                        </th>
                        <th className="px-3 py-2 text-right">
                          {labels.incomeNet}
                        </th>
                        <th className="px-3 py-2 text-right">
                          {labels.expenseNet}
                        </th>
                        <th className="px-3 py-2 text-right">
                          {labels.vatBalance}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((row) => (
                        <tr
                          key={row.id}
                          className="border-t border-slate-100 dark:border-slate-800"
                        >
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatDateTime(row.finishedAt)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatDate(row.dateFrom)} –{' '}
                            {formatDate(row.dateTo)}
                          </td>
                          <td className="px-3 py-2">
                            <Badge
                              variant={
                                row.status === 'success' ? 'success' : 'danger'
                              }
                            >
                              {row.status === 'success'
                                ? labels.syncStatusSuccess
                                : labels.syncStatusError}
                            </Badge>
                            {row.errorMessage ? (
                              <div className="mt-1 max-w-[12rem] truncate text-xs text-rose-600 dark:text-rose-400">
                                {row.errorMessage}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.documentsSaved}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.incomeNet != null
                              ? formatMoney(row.incomeNet)
                              : '—'}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.expenseNet != null
                              ? formatMoney(row.expenseNet)
                              : '—'}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.vatPayable != null
                              ? formatMoney(row.vatPayable)
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </DataTablePanel>
          )}
        </section>
      </div>
    </MydataPageShell>
  );
}
